import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Maximize, Minimize } from 'lucide-react';

// Variable global para trackear si la API ya está cargada
let isYouTubeAPIReady = false;
const apiReadyCallbacks = [];

// Cargar la API de YouTube una sola vez
if (typeof window !== 'undefined' && !window.YT) {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
        isYouTubeAPIReady = true;
        // Ejecutar todos los callbacks pendientes
        apiReadyCallbacks.forEach(callback => callback());
        apiReadyCallbacks.length = 0;
    };
}

const VideoPlayer = ({ channel, autoplay, mute = true }) => {
    const [player, setPlayer] = useState(null);
    const [isMuted, setIsMuted] = useState(mute);
    const [isReady, setIsReady] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isContainerHovered, setIsContainerHovered] = useState(false);
    const playerRef = useRef(null);
    const iframeId = useRef(`youtube-player-${channel.id}-${Math.random()}`);
    const containerRef = useRef(null);

    useEffect(() => {
        if (channel.type !== 'youtube') return;

        const initPlayer = () => {
            // Verificar que el contenedor existe antes de crear el player
            if (!containerRef.current) return;

            let hasSeeked = false; // Flag para seekear al live solo una vez

            try {
                playerRef.current = new window.YT.Player(iframeId.current, {
                    videoId: channel.videoId,
                    playerVars: {
                        autoplay: autoplay ? 1 : 0,
                        mute: mute ? 1 : 0,
                        controls: 0,
                        rel: 0,
                        modestbranding: 1,
                        playsinline: 1,
                        enablejsapi: 1,
                        fs: 0, // Deshabilitar el botón de fullscreen nativo
                        iv_load_policy: 3, // Deshabilitar anotaciones
                        disablekb: 1, // Deshabilitar controles de teclado
                    },
                    events: {
                        onReady: (event) => {
                            setPlayer(event.target);
                            setIsReady(true);
                            if (mute) {
                                event.target.mute();
                            }
                            if (autoplay) {
                                event.target.playVideo();
                            }
                        },
                        onStateChange: (event) => {
                            // Al primer play, seekear al borde del live.
                            // hasSeeked evita el bucle infinito que genera el propio seekTo.
                            // Luego de 5s se resetea por si el stream quedó atrasado.
                            if (event.data === window.YT.PlayerState.PLAYING && !hasSeeked) {
                                hasSeeked = true;
                                const duration = event.target.getDuration();
                                if (duration > 0) {
                                    event.target.seekTo(duration, true);
                                }
                                //Para mantener siempre actualizado pero evitando comportamientos extraños tipo remix electrónico.
                                setTimeout(() => { hasSeeked = false; }, 15_000);
                            }
                        },
                        onError: (event) => {
                            console.error('YouTube Player Error:', event.data);
                        }
                    }
                });
            } catch (error) {
                console.error('Error creating YouTube player:', error);
            }
        };

        // Si la API ya está lista, inicializar inmediatamente
        if (isYouTubeAPIReady && window.YT && window.YT.Player) {
            initPlayer();
        } else {
            // Si no, agregar a la cola de callbacks
            apiReadyCallbacks.push(initPlayer);
        }

        return () => {
            if (playerRef.current && typeof playerRef.current.destroy === 'function') {
                try {
                    playerRef.current.destroy();
                } catch (error) {
                    console.error('Error destroying player:', error);
                }
            }
        };
    }, [channel.videoId, channel.type]);

    // Listener para detectar cambios en fullscreen
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Manejar doble click para toggle fullscreen
    useEffect(() => {
        const handleDoubleClick = (e) => {
            // Solo si no se hizo click en los botones
            if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
                return;
            }

            e.preventDefault();
            e.stopPropagation();
            toggleFullscreen();
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('dblclick', handleDoubleClick);
            return () => container.removeEventListener('dblclick', handleDoubleClick);
        }
    }, [isFullscreen]); // Agregar isFullscreen como dependencia

    //Para controlar el mute/unmute de manera externa, desde el padre y que funcione, y no quede en pausa.
    useEffect(() => {
        if (player && isReady) {
            // Verificar si el video está reproduciendo
            const playerState = player.getPlayerState();
            const isPlaying = playerState === 1; // 1 = playing

            if (mute) {
                player.mute();
                setIsMuted(true);
            } else {
                player.unMute();
                setIsMuted(false);
            }

            // Si el video estaba reproduciendo y se detuvo, volver a reproducir
            setTimeout(() => {
                const currentState = player.getPlayerState();
                if (isPlaying && currentState !== 1) {
                    player.playVideo();
                }
            }, 100);
        }
    }, [mute, player, isReady]);

    const toggleMute = () => {
        if (player && isReady) {
            if (isMuted) {
                player.unMute();
                setIsMuted(false);
            } else {
                player.mute();
                setIsMuted(true);
            }
        }
    };

    const toggleFullscreen = () => {
        if (!containerRef.current) return;

        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch(err => {
                console.error('Error al intentar pantalla completa:', err);
            });
        } else {
            document.exitFullscreen();
        }
    };

    const playerStyle = {
        width: '100%',
        height: '100%',
        border: 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'auto'
    };

    const buttonStyle = {
        position: 'absolute',
        bottom: '12px',
        background: 'rgba(0, 0, 0, 0.08)',
        border: `1px solid ${isContainerHovered ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 10,
        transition: 'all 0.25s ease',
        color: isContainerHovered ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.4)',
    };

    if (channel.type === 'youtube') {
        return (
            <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%' }} onMouseEnter={() => setIsContainerHovered(true)} onMouseLeave={() => setIsContainerHovered(false)}>
                <div id={iframeId.current} style={playerStyle} />

                {isReady && (
                    <>
                        <button
                            onClick={toggleFullscreen}
                            style={{ ...buttonStyle, right: '60px' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.75)'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
                        >
                            {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
                        </button>

                        <button
                            onClick={toggleMute}
                            style={{ ...buttonStyle, right: '12px' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.75)'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
                        >
                            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                        </button>
                    </>
                )}
            </div>
        );
    } else if (channel.type === 'twitch') {
        return (
            <iframe
                style={playerStyle}
                src={`https://player.twitch.tv/?channel=${channel.channel}&parent=${window.location.hostname}&muted=true`}
                allowFullScreen
            />
        );

    } else if (channel.type === 'iframe') {
        return (
            <iframe
                style={playerStyle}
                src={channel.url}
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
        );
    } else if (channel.type === 'custom-component') {
        const CustomComponent = channel.render;
        return <CustomComponent />;
    }

    return null;
};

export default VideoPlayer;